import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import moment from "moment-timezone";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  Paper,
  InputAdornment,
  makeStyles
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { CSVLink } from "react-csv";
import { getGroupCampaignShipping } from "../../services/groupCampaigns.api";
import { toast } from "react-toastify";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTimezone } from "../../hooks/useTimezone";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      width: "90%",
      maxWidth: "1200px",
      maxHeight: "90vh"
    }
  },
  dialogTitle: {
    backgroundColor: theme.mode === "dark" ? "#1f2937" : "#f5f5f5",
    borderBottom: `1px solid ${theme.mode === "dark" ? "#374151" : "#e0e0e0"}`,
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  dialogContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.mode === "dark" ? "#111827" : "#fff"
  },
  filtersContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center"
  },
  table: {
    minWidth: 650,
    "& .MuiTableCell-head": {
      backgroundColor: theme.mode === "dark" ? "#1f2937" : "#f5f5f5",
      fontWeight: "bold",
      color: theme.palette.text.primary
    },
    "& .MuiTableRow-root:hover": {
      backgroundColor: theme.mode === "dark" ? "#374151" : "#f9f9f9"
    }
  },
  statusChip: {
    fontWeight: "bold"
  },
  messageCell: {
    maxWidth: "300px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4)
  }
}));

const GroupCampaignExecutionsModal = ({ open, onClose, campaignId, campaignName }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { socketManager } = useContext(SocketContext);
  const { formatDateTime, getConfiguredTimezone } = useTimezone();
  const isMounted = useRef(true);
  const updateIntervalRef = useRef(null);

  const [shippings, setShippings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState("");
  const [selectedMediaUrl, setSelectedMediaUrl] = useState(null);
  const [selectedMediaName, setSelectedMediaName] = useState(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (open && campaignId) {
      loadShippings();
      
      // Actualización automática cada 3 segundos
      updateIntervalRef.current = setInterval(() => {
        loadShippings(true); // true = silent update (no loading spinner)
      }, 3000);
    } else {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    }

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId, page, rowsPerPage, filter, searchTerm]);

  // Socket.IO para actualizaciones en tiempo real
  useEffect(() => {
    if (!open || !campaignId || !socketManager) return;

    const companyId = user?.companyId;
    if (!companyId) return;

    const socket = socketManager.getSocket(companyId);

    const handleCampaignUpdate = (data) => {
      if (data.record && data.record.id === parseInt(campaignId)) {
        // Recargar envíos cuando la campaña se actualiza
        loadShippings(true);
      }
    };

    socket.on(`company-${companyId}-groupCampaign`, handleCampaignUpdate);

    return () => {
      socket.off(`company-${companyId}-groupCampaign`, handleCampaignUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId, socketManager, user]);

  const loadShippings = async (silent = false) => {
    if (!silent) setLoading(true);

    try {
      const { data } = await getGroupCampaignShipping(campaignId, {
        status: filter === "all" ? undefined : filter,
        search: searchTerm || undefined,
        page: page + 1,
        limit: rowsPerPage
      });

      if (isMounted.current) {
        setShippings(data.shippings);
        setTotal(data.total);
      }
    } catch (err) {
      console.error("Error loading shippings:", err);
      if (!silent) {
        toast.error("Error al cargar los envíos");
      }
    } finally {
      if (!silent && isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleViewMessage = (message, mediaUrl = null, mediaName = null) => {
    setSelectedMessage(message);
    setSelectedMediaUrl(mediaUrl);
    setSelectedMediaName(mediaName);
    setMessageModalOpen(true);
  };

  const formatDate = useCallback((date) => {
    if (!date) return "-";
    const tz = getConfiguredTimezone() || "America/Lima";
    const s = String(date).replace("T", " ").split(".")[0];
    const m = moment.tz(s, "YYYY-MM-DD HH:mm:ss", tz);
    return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : formatDateTime(date) || "-";
  }, [getConfiguredTimezone, formatDateTime]);

  const getStatusChip = (shipping) => {
    if (shipping.status === "sent" && shipping.sentAt) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Entregado"
          size="small"
          className={classes.statusChip}
          style={{ backgroundColor: "#4caf50", color: "white" }}
        />
      );
    } else if (shipping.status === "failed") {
      return (
        <Chip
          icon={<ErrorIcon />}
          label="Fallido"
          size="small"
          className={classes.statusChip}
          style={{ backgroundColor: "#f44336", color: "white" }}
        />
      );
    } else {
      return (
        <Chip
          label="Pendiente"
          size="small"
          className={classes.statusChip}
          style={{ backgroundColor: "#ff9800", color: "white" }}
        />
      );
    }
  };

  const getCSVData = () => {
    return shippings.map((shipping) => ({
      "ID Grupo": shipping.groupId,
      "Nombre Grupo": shipping.groupDetails?.name || "-",
      Estado: shipping.status === "sent" ? "Entregado" : shipping.status === "failed" ? "Fallido" : "Pendiente",
      "Fecha Envío": formatDate(shipping.sentAt),
      "Número Ejecución": shipping.executionNumber || "-",
      Mensaje: shipping.message || "-",
      "Archivo Adjunto": shipping.mediaName || "-",
      "URL Archivo": shipping.mediaUrl || "-",
      Error: shipping.error || "-"
    }));
  };

  const csvHeaders = [
    { label: "ID Grupo", key: "ID Grupo" },
    { label: "Nombre Grupo", key: "Nombre Grupo" },
    { label: "Estado", key: "Estado" },
    { label: "Fecha Envío", key: "Fecha Envío" },
    { label: "Número Ejecución", key: "Número Ejecución" },
    { label: "Mensaje", key: "Mensaje" },
    { label: "Archivo Adjunto", key: "Archivo Adjunto" },
    { label: "URL Archivo", key: "URL Archivo" },
    { label: "Error", key: "Error" }
  ];

  const sentCount = shippings.filter(s => s.status === "sent").length;
  const failedCount = shippings.filter(s => s.status === "failed").length;
  const pendingCount = shippings.filter(s => s.status === "pending").length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        className={classes.dialog}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box>
            <Typography variant="h6" component="div">
              Detalle de Envíos - {campaignName || `Campaña #${campaignId}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {sentCount} de {total} grupos enviados
              {failedCount > 0 && ` • ${failedCount} fallidos`}
              {pendingCount > 0 && ` • ${pendingCount} pendientes`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          {/* Filtros y búsqueda */}
          <Box className={classes.filtersContainer}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select value={filter} onChange={handleFilterChange} label="Estado">
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="sent">Enviados</MenuItem>
                <MenuItem value="failed">Fallidos</MenuItem>
                <MenuItem value="pending">Pendientes</MenuItem>
              </Select>
            </FormControl>

            <TextField
              variant="outlined"
              size="small"
              placeholder="Buscar por ID o nombre de grupo..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              style={{ flexGrow: 1, maxWidth: 400 }}
            />

            <CSVLink
              data={getCSVData()}
              headers={csvHeaders}
              filename={`envios-grupos-campana-${campaignId}-${Date.now()}.csv`}
              style={{ textDecoration: "none" }}
            >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GetAppIcon />}
                size="small"
              >
                Exportar CSV
              </Button>
            </CSVLink>
          </Box>

          {/* Tabla de envíos */}
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : shippings.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6" gutterBottom>
                {filter === "all"
                  ? "Aún no hay registros de envío"
                  : `No hay envíos ${filter === "sent" ? "enviados" : filter === "failed" ? "fallidos" : "pendientes"}`}
              </Typography>
              <Typography variant="body2">
                {filter === "all"
                  ? "La campaña comenzará a enviar mensajes según la programación."
                  : "Intenta cambiar los filtros para ver más resultados."}
              </Typography>
            </Box>
          ) : (
            <Paper variant="outlined">
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID Grupo</TableCell>
                    <TableCell>Nombre Grupo</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell>Fecha Envío</TableCell>
                    <TableCell align="center">Ejecución</TableCell>
                    <TableCell>Mensaje</TableCell>
                    <TableCell>Archivo</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shippings.map((shipping) => (
                    <TableRow key={shipping.id}>
                      <TableCell>{shipping.groupId}</TableCell>
                      <TableCell>{shipping.groupDetails?.name || "-"}</TableCell>
                      <TableCell align="center">
                        {getStatusChip(shipping)}
                      </TableCell>
                      <TableCell>{formatDate(shipping.sentAt)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={shipping.executionNumber || "-"}
                          size="small"
                          color={shipping.executionNumber ? "primary" : "default"}
                        />
                      </TableCell>
                      <TableCell className={classes.messageCell}>
                        {shipping.message
                          ? shipping.message.substring(0, 60) + (shipping.message.length > 60 ? "..." : "")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {shipping.mediaUrl ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachFileIcon fontSize="small" color="primary" />
                            <Typography variant="body2" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {shipping.mediaName || "Archivo adjunto"}
                            </Typography>
                            <Tooltip title="Abrir archivo">
                              <IconButton
                                size="small"
                                onClick={() => window.open(shipping.mediaUrl, "_blank")}
                                style={{ padding: 4 }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {shipping.message && (
                          <Tooltip title="Ver mensaje completo">
                            <IconButton
                              size="small"
                              onClick={() => handleViewMessage(shipping.message, shipping.mediaUrl, shipping.mediaName)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
              />
            </Paper>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para ver mensaje completo */}
      <Dialog
        open={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mensaje Completo
          <IconButton
            onClick={() => setMessageModalOpen(false)}
            style={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" style={{ whiteSpace: "pre-wrap", marginBottom: selectedMediaUrl ? 2 : 0 }}>
            {selectedMessage}
          </Typography>
          {selectedMediaUrl && (
            <Box style={{ marginTop: 16, padding: 16, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
              <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                <AttachFileIcon color="primary" style={{ marginRight: 8 }} />
                <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                  Archivo adjunto:
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="body2" style={{ flex: 1 }}>
                  {selectedMediaName || "Archivo multimedia"}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(selectedMediaUrl, "_blank")}
                >
                  Abrir archivo
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageModalOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupCampaignExecutionsModal;
